import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { BASE_URL as API_BASE_URL } from "../api/api";
import { Plus, Trash2, MapPin, Package, FileText, Calculator, ArrowLeft, Paperclip, Download, X, MessageSquare } from "lucide-react";
import SaveOptionsDialog, { useSaveOptions } from "./Saveoptionsdialog";

let uid = 1;
const nextId = () => uid++;

const emptyProductRow = () => ({
  id: nextId(),
  oem: "",
  product: "",
  qty: "",
  unitPrice: "",
  vendorUnitPrice: "",
  shippingAddressId: "",
});

const emptyShippingAddress = () => ({
  id: nextId(),
  label: "",
  name: "",
  contact: "",
  email: "",
  addressLine: "",
  gst: "",
});

// The backend/API only knows about a single free-text "address" column per
// shipping address. To avoid a schema change, Name / Contact / Email /
// Address / GST are packed into that same string, separated by
// ADDRESS_SEPARATOR below, and unpacked again when a draft is loaded back
// in (see splitPackedAddress below). This is also what's shown live in the
// "Full shipping address" preview box as the person fills in the other
// fields.
//
// IMPORTANT: keep this a plain ASCII token with no leading/trailing
// whitespace and no multi-byte characters. If the first field (Name) is
// left blank, the packed string starts with the separator - and if it has
// a leading space (e.g. " || " or " 🔸 "), any backend/DB that trims
// whitespace on save (very common: Mongoose `trim: true`, DB triggers,
// a stray .trim() call) will silently strip that leading space, which
// then breaks every split on reload and leaves the separator character
// stuck onto the next field's value. A bare "||" is immune to trimming
// (trim only removes whitespace, never "|") and safe for any DB charset.
const ADDRESS_SEPARATOR = "🔸";
// Drafts saved by an earlier version of this form may have used a
// different separator - try those too so old data still loads correctly.
const LEGACY_ADDRESS_SEPARATORS = [" 🔸 ", "//", "||"];

// When a field is left blank, its own label is written into the packed
// string in its place (e.g. "Contact") instead of leaving a blank gap -
// purely so the auto-generated preview reads clearly rather than showing
// stray separators next to nothing. As soon as the person actually types
// a value into that field, the real value takes over and overrides it.
const ADDRESS_FIELD_LABELS = ["Name", "Contact", "Email", "Address", "GST"];

const packAddress = (a) => {
  const values = [a.name, a.contact, a.email, a.addressLine, a.gst];
  return values
    .map((v, i) => (v && String(v).trim() ? v : ADDRESS_FIELD_LABELS[i]))
    .join(ADDRESS_SEPARATOR);
};

const splitPackedAddress = (packed) => {
  const str = String(packed ?? "");
  const sep = str.includes(ADDRESS_SEPARATOR)
    ? ADDRESS_SEPARATOR
    : LEGACY_ADDRESS_SEPARATORS.find((s) => str.includes(s)) || ADDRESS_SEPARATOR;
  const parts = str.split(sep);
  // A part that's still exactly the placeholder label means the field was
  // never actually filled in - treat it as blank again when re-editing.
  const unfallback = (v, i) => (v === ADDRESS_FIELD_LABELS[i] ? "" : v || "");
  return {
    name: unfallback(parts[0], 0),
    contact: unfallback(parts[1], 1),
    email: unfallback(parts[2], 2),
    addressLine: unfallback(parts[3], 3),
    gst: unfallback(parts[4], 4),
  };
};

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// The lead id shows up in two shapes in this app: raw ("15", from the
// route/localStorage) and display-formatted ("LD-0015", from wherever a
// page passes leadData.leadId straight through). The backend route only
// accepts the raw numeric form, so anything used in an API call goes
// through this first.
const toRawLeadId = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const match = String(v).match(/(\d+)/);
  return match ? String(parseInt(match[1], 10)) : "";
};

// Last-resort fallback: pull the numeric id straight out of the URL
// (e.g. /leads/17/raise-opf -> "17"). This means the draft lookup still
// works even if whatever page renders this component doesn't pass a
// leadId prop down at all.
const leadIdFromUrl = () => {
  if (typeof window === "undefined") return "";
  const match = window.location.pathname.match(/\/leads\/(\d+)/);
  return match ? match[1] : "";
};

// Formats a "YYYY-MM-DD HH:MM:SS" string from the API as e.g.
// "25 Sep 2026, 5:11 PM". Parsed by hand (not new Date(string)) so the
// browser's timezone can never shift the time. Older comments that were
// saved with only a date (time 00:00:00) show the date alone.
const formatCommentTime = (value) => {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!m) return "";
  const [, y, mo, d, h = "00", mi = "00"] = m;
  const date = new Date(+y, +mo - 1, +d, +h, +mi);
  const day = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  if (h === "00" && mi === "00") return day;
  const time = date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}, ${time.toUpperCase()}`;
};

const fmt = (v) =>
  "₹" +
  v.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });

const fmtPct = (v) => (isFinite(v) ? (v * 100).toFixed(2) + "%" : "—");

// Formats a numeric string using the Indian digit-grouping system for
// display inside editable money inputs (e.g. 1234567 -> "12,34,567")
const formatIndianCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const sanitized = String(value).replace(/[^0-9.]/g, "");
  if (sanitized === "") return "";

  const [integerPartRaw, ...decimalParts] = sanitized.split(".");
  const decimalPart = decimalParts.length ? decimalParts.join("") : undefined;
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "") || "0";

  let formattedInteger;
  if (integerPart.length <= 3) {
    formattedInteger = integerPart;
  } else {
    const lastThree = integerPart.slice(-3);
    const remaining = integerPart.slice(0, -3);
    formattedInteger = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  }

  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

// Strips comma separators back out, returning the raw numeric string that
// actually gets stored in state / sent to the API
const unformatCurrency = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).replace(/,/g, "");
};

const PAYMENT_TERMS_OPTIONS = ["Advance","Immediate","NT30","NT45", "NT60", "NT75", "NT120"];
const DROPSHIPMENT_OPTIONS = ["Yes", "No"];
const GST_OPTIONS = ["GST Not Applicable"]

// Calls the Flask backend's /api/opf/save or /api/opf/submit endpoint.
// Assumes the JWT issued by your auth flow is stored in localStorage under
// "token" (the common pattern) — adjust the key if your app stores it elsewhere,
// e.g. in a cookie or an auth context/provider.
// API_BASE_URL (host only, no /api suffix) is the same single source of
// truth ../api/api.js uses for its own axios instance — imported above
// instead of re-reading REACT_APP_API_BASE_URL here, so there's one place
// to change the backend host.
async function callOpfApi(endpoint, payload) {
  const token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
  const res = await fetch(`${API_BASE_URL}/api/opf/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed with status ${res.status}`);
  }
  return data;
}
const ADD_NEW_VALUE = "__add_new__";

function CustomerNameField({
  value,
  onChange,
  customers,
  onAddCustomer,
  disabled = false,
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const confirmAdd = () => {
    const name = draft.trim();
    if (name) {
      onAddCustomer(name);
      onChange(name);
    }
    setDraft("");
    setAdding(false);
  };

  return (
    <div style={{ marginBottom: "14px" }}>
      <label
        style={{
          display: "block",
          fontSize: "12.5px",
          fontWeight: 500,
          color: "#475569",
          marginBottom: "5px",
        }}
      >
        Customer name
      </label>
      {adding ? (
        <div style={{ display: "flex", gap: "6px" }}>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="New customer name"
            onKeyDown={(e) => e.key === "Enter" && confirmAdd()}
            style={{
              flex: 1,
              padding: "8px 10px",
              fontSize: "13.5px",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              boxSizing: "border-box",
              color: "#0f172a",
            }}
          />
          <button
            onClick={confirmAdd}
            style={{
              padding: "0 12px",
              fontSize: "12.5px",
              fontWeight: 500,
              color: "#fff",
              background: "#1e3a5f",
              border: "1px solid #1e3a5f",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Add
          </button>
          <button
            onClick={() => {
              setAdding(false);
              setDraft("");
            }}
            style={{
              padding: "0 12px",
              fontSize: "12.5px",
              fontWeight: 500,
              color: "#475569",
              background: "#fff",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.value === ADD_NEW_VALUE) {
              setAdding(true);
            } else {
              onChange(e.target.value);
            }
          }}
           style={{
    width: "100%",
    padding: "8px 10px",
    fontSize: "13.5px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#0f172a",
    background: disabled ? "#f1f5f9" : "#fff",
    cursor: disabled ? "not-allowed" : "pointer",

    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    backgroundImage: disabled ? "none" : undefined,
  }}
        >
          <option value="">Select customer…</option>
          {customers.map((c) => (
  <option key={c.id || c.name} value={c.name}>
    {c.name}
  </option>
))}
          <option value={ADD_NEW_VALUE}>+ Add new customer…</option>
        </select>
      )}
    </div>
  );
}
function SelectField({ label, value, onChange, options, disabled = false }) {
  return (
    <div style={{ marginBottom: "14px" }}>
      <label
        style={{
          display: "block",
          fontSize: "12.5px",
          fontWeight: 500,
          color: "#475569",
          marginBottom: "5px",
        }}
      >
        {label}
      </label>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          fontSize: "13.5px",
          border: "1px solid #cbd5e1",
          borderRadius: "6px",
          boxSizing: "border-box",
          color: "#0f172a",
          background: disabled ? "#f1f5f9" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
function SuggestionField({ label, value, onChange, options, disabled = false }) {
  const listId = `${label.replace(/\s+/g, "-").toLowerCase()}-list`;

  return (
    <div style={{ marginBottom: "14px" }}>
      <label
        style={{
          display: "block",
          fontSize: "12.5px",
          fontWeight: 500,
          color: "#475569",
          marginBottom: "5px",
        }}
      >
        {label}
      </label>

      <input
        list={listId}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type or select..."
        style={{
          width: "100%",
          padding: "8px 10px",
          fontSize: "13.5px",
          border: "1px solid #cbd5e1",
          borderRadius: "6px",
          boxSizing: "border-box",
          color: "#0f172a",
          background: disabled ? "#f1f5f9" : "#fff",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />

      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </div>
  );
}
function Field({ label, value, onChange, placeholder, textarea, type = "text", disabled = false }) {
  return (
    <div style={{ marginBottom: "14px" }}>
      <label
        style={{
          display: "block",
          fontSize: "12.5px",
          fontWeight: 500,
          color: "#475569",
          marginBottom: "5px",
        }}
      >
        {label}
      </label>
      {textarea ? (
        <textarea
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          style={{
            width: "100%",
            padding: "8px 10px",
            fontSize: "13.5px",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            resize: "vertical",
            fontFamily: "inherit",
            boxSizing: "border-box",
            color: "#0f172a",
            background: disabled ? "#f1f5f9" : "#fff",
          }}
        />
      ) : (
        <input
          type={type}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "8px 10px",
            fontSize: "13.5px",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            fontFamily: "inherit",
            boxSizing: "border-box",
            color: "#0f172a",
            background: disabled ? "#f1f5f9" : "#fff",
          }}
        />
      )}
    </div>
  );
}

function AddrField({ label, value, onChange, placeholder, type = "text", disabled = false }) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: "11.5px",
          fontWeight: 500,
          color: "#64748b",
          marginBottom: "5px",
        }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          fontSize: "13px",
          border: "1px solid #cbd5e1",
          borderRadius: "6px",
          boxSizing: "border-box",
          color: "#0f172a",
          fontFamily: "inherit",
          background: disabled ? "#f1f5f9" : "#fff",
        }}
      />
    </div>
  );
}

// Same combo-box pattern as SuggestionField (type-or-pick via a native
// datalist), but sized/styled to match the other AddrField inputs used
// inside a shipping address block, and taking an explicit listId since
// there can be several shipping addresses - each needs its own datalist
// rather than sharing one keyed off the label.
function AddrSuggestionField({ label, value, onChange, options = [], placeholder, type = "text", disabled = false, listId }) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: "11.5px",
          fontWeight: 500,
          color: "#64748b",
          marginBottom: "5px",
        }}
      >
        {label}
      </label>
      <input
        type={type}
        list={listId}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          fontSize: "13px",
          border: "1px solid #cbd5e1",
          borderRadius: "6px",
          boxSizing: "border-box",
          color: "#0f172a",
          fontFamily: "inherit",
          background: disabled ? "#f1f5f9" : "#fff",
        }}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
      <div
        style={{
          width: "30px",
          height: "30px",
          borderRadius: "7px",
          background: "#1e3a5f",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={16} color="#fff" />
      </div>
      <div>
        <h2 style={{ margin: 0, fontSize: "15.5px", fontWeight: 600, color: "#0f172a" }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{ margin: "1px 0 0", fontSize: "12px", color: "#64748b" }}>{subtitle}</p>
        )}
      </div>
    </div>
  );
}

export default function AddDealSales({
  defaultCustomerName = "",
  defaultBillingAddress = "",
  leadId = "",
  leadStatus = "",
  onSaveDetails,
  onSubmit,
}) {
  const navigate = useNavigate();

  const storedCustomerName = (() => {
  return localStorage.getItem("customer_name") || "";
})();

const [header, setHeader] = useState({
  customerName: storedCustomerName || defaultCustomerName,
  distributorName: "",
  billingAddress: defaultBillingAddress,
  product: "",
  contactPerson: "",
  vendorPaymentTerms: "",
  dropshipment: "",
  phone: "",
  email: "",
  poNo: "",
  customerPaymentTerms: "",
  deliveryTerms: "",
  locationImplementation: "",
  locationOnsite: "",
});

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  // Suggestions for the shipping-address GST field: the related
  // customer's own GST plus any GST(s) already used on that customer's
  // earlier OPFs. Refetched whenever the selected customer changes.
  const [gstSuggestions, setGstSuggestions] = useState([]);
  // Suggestions for the shipping-address Name / Contact / Email / Address
  // fields. Built on the backend from the customer + its points of
  // contact, the opportunity + its points of contact, and the shipping
  // addresses of earlier OPFs for this customer. Every list is already
  // de-duplicated server-side.
  const [contactSuggestions, setContactSuggestions] = useState({
    names: [],
    contacts: [],
    emails: [],
    addresses: [],
  });

const [storedLeadId] = useState(() => {
  return localStorage.getItem("lead_id") || "";
});

const [storedLeadStatus] = useState(() => {
  return localStorage.getItem("lead_status") || "";
});

// Single source of truth for "which lead is this OPF tied to" used by
// every API call below - normalized so it doesn't matter whether it
// arrived as a raw id, a "LD-0015" display string, or came from
// localStorage instead.
//
// The URL goes first, not last: /leads/<id>/raise-opf is how every entry
// point into this page navigates here - the opportunity list, and the
// "Review OPF" link in emails - so it's the one value that's guaranteed
// to describe *this* visit rather than a previous one. storedLeadId is
// leftover from whatever lead was last opened and never gets cleared on
// navigation, so putting it first meant opening opportunity 21 from an
// email while opportunity 15 was still cached would silently load 15's
// OPF instead - the page would render, just against the wrong lead. Now
// localStorage only fills in for the (rare) case this component is
// rendered somewhere without that URL pattern at all.
const effectiveLeadId =
  leadIdFromUrl() || toRawLeadId(leadId) || toRawLeadId(storedLeadId);

// Same idea as effectiveLeadId above: prefer whatever's in localStorage
// (kept in sync by LeadDetails whenever it loads the lead), falling back
// to the leadStatus prop if localStorage hasn't been populated yet.
const effectiveLeadStatus = storedLeadStatus || leadStatus;

// Submit is only meaningful once the lead has actually been won — while
// it's still Ongoing, this form should only let the user Save details.
// (Combined with the approval-status check further below, once that
// state exists, into the actual isSubmitAllowed used by the Submit button.)
const isLeadWon = effectiveLeadStatus?.toLowerCase() === "won";
  const addCustomer = (name) =>
  setCustomers((cs) =>
    cs.some((c) => c.name === name)
      ? cs
      : [...cs, { id: "", name, gst: "" }]
  );
  

  const [shippingAddresses, setShippingAddresses] = useState([emptyShippingAddress()]);
  const [rows, setRows] = useState([{ ...emptyProductRow(), oem: "" }]);

  const [charges, setCharges] = useState({
    projectManagementCharges: "",
    implementationCharges: "",
    postImplementationSupportCharges: "",
    costOfFinancePBG: "",
    logisticInsurance: "",
    gemTenderPOValue: "",
  });

  const setH = (key) => (val) => setHeader((p) => ({ ...p, [key]: val }));
  const setC = (key) => (val) => setCharges((p) => ({ ...p, [key]: val }));

  const updateRow = (id, key, val) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [key]: val } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyProductRow()]);
  const removeRow = (id) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));

  const updateAddr = (id, key, val) =>
    setShippingAddresses((as) => as.map((a) => (a.id === id ? { ...a, [key]: val } : a)));
  const addAddr = () => setShippingAddresses((as) => [...as, emptyShippingAddress()]);
  const removeAddr = (id) =>
    setShippingAddresses((as) => (as.length > 1 ? as.filter((a) => a.id !== id) : as));

  // A charge field counts as "manually overridden" once the user has typed
  // something into it (including 0). Only truly untouched (undefined/null -
  // i.e. never loaded/edited) falls back to the auto-computed default.
  // Explicitly clearing the field back to "" must NOT fall back to the
  // default - it means the user wants this charge to be 0, not
  // recalculated/prefilled again.
  const overrideOr = (raw, defaultVal) =>
    raw === undefined || raw === null ? defaultVal : num(raw);

  // ---- Replicating the Excel formulas exactly ----
  const calc = useMemo(() => {
    const rowsCalc = rows.map((r) => {
      const qty = num(r.qty);
      const unitPrice = num(r.unitPrice);
      const vendorUnitPrice = num(r.vendorUnitPrice);
      const totalPOValue = qty * unitPrice; // F = D*E
      const totalPOValueVendor = qty * vendorUnitPrice; // H = G*D
      return { ...r, totalPOValue, totalPOValueVendor };
    });

    const endUserPOValue = rowsCalc.reduce((s, r) => s + r.totalPOValue, 0); // F28 = SUM(F14:F27)
    const vendorPOValue = rowsCalc.reduce((s, r) => s + r.totalPOValueVendor, 0); // H28 = SUM(H14:H27)
    const grossMargin = endUserPOValue - vendorPOValue; // I14 = SUM(F14:F27)-SUM(H14:H27)
    const grossMarginPct = endUserPOValue !== 0 ? grossMargin / endUserPOValue : NaN; // I28 = I14/F28

    const projectManagementCharges = num(charges.projectManagementCharges); // C29
    const miscCharges = overrideOr(charges.miscCharges, projectManagementCharges * 0.1); // C30 = C29*10%, editable
    const implementationCharges = num(charges.implementationCharges); // C31
    const postImplementationSupportCharges = num(charges.postImplementationSupportCharges); // C32
    const costOfFinancePBG = num(charges.costOfFinancePBG); // C33
    const costFinance1Yr = overrideOr(charges.costFinance1Yr, endUserPOValue * 0.03125); // C34 = F28*3.125%, editable
    const costFinance2Yr = overrideOr(charges.costFinance2Yr, endUserPOValue * 0.1125); // C35 = F28*11.25%, editable
    const costFinance3Yr = overrideOr(charges.costFinance3Yr, endUserPOValue * 0.24375); // C36 = F28*24.375%, editable
    const logisticInsurance = num(charges.logisticInsurance); // C37
    const distributorMargin = overrideOr(charges.distributorMargin, vendorPOValue * 0.005); // C38 = H28*0.5%, editable
    const gemTenderPOValue = num(charges.gemTenderPOValue); // C39
    const gemFees = overrideOr(charges.gemFees, gemTenderPOValue * 0.005); // C40 = C39*0.5%, editable

    // C41 = I14 - C29 - C30 - C31 - C32 - C33 - C34 - C35 - C36 - C37 - C38 - C40
    const netMargin =
      grossMargin -
      projectManagementCharges -
      miscCharges -
      implementationCharges -
      postImplementationSupportCharges -
      costOfFinancePBG -
      costFinance1Yr -
      costFinance2Yr -
      costFinance3Yr -
      logisticInsurance -
      distributorMargin -
      gemFees;

    return {
      rowsCalc,
      endUserPOValue,
      vendorPOValue,
      grossMargin,
      grossMarginPct,
      miscCharges,
      costFinance1Yr,
      costFinance2Yr,
      costFinance3Yr,
      distributorMargin,
      gemFees,
      netMargin,
    };
  }, [rows, charges]);

  const [status, setStatus] = useState(null); // { type: 'saved' | 'submitted' | 'error', message }

  // The status banner now lives at the top of the form, but Save/Submit
  // are clicked from the sticky action bar at the bottom - scroll up so
  // the message is actually visible when it appears.
  useEffect(() => {
    if (status) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [status]);
  const [opfId, setOpfId] = useState(null); // set once the backend has created the row
  const [opfNumber, setOpfNumber] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);

  // ---- Send for Approval --------------------------------------------------
  // Works like the existing "Save details -> email admin" flow, except the
  // email the admin gets contains Approve / Decline buttons. Whatever the
  // admin clicks flips `approval_status` on the backend record; once a
  // request has gone out, the "Send for Approval" button is replaced by a
  // status pill reflecting that value ("pending" | "approved" | "declined").
  const [approvalStatus, setApprovalStatus] = useState(null); // null | "pending" | "approved" | "declined"
  const [sendingApproval, setSendingApproval] = useState(false);

  // Approve/Decline used to only exist as one-click links in the approval
  // email. They now also show up right here on the OPF page - admin-only,
  // and only while a request is still "pending" - so a decision can be
  // made without leaving the app. The emailed link just brings the admin
  // to this page; this is what actually records the decision
  // (PATCH /opf/<opfId>/approval/decide, admin-only on the backend too).
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = (currentUser.department || "").toLowerCase() === "admin";
  const [decidingApproval, setDecidingApproval] = useState(false);

  const handleDecideApproval = async (decision) => {
    if (!opfId || decidingApproval) return;
    setDecidingApproval(true);
    try {
      const res = await api.patch(`/opf/${opfId}/approval/decide`, { decision });
      const next = res.data?.approvalStatus || decision;
      setApprovalStatus(next);
      const baseMsg = next === "approved" ? "You approved this OPF." : "You declined this OPF.";
      const emailNote = res.data?.emailSent
        ? " The account manager has been notified by email."
        : " (Could not notify the account manager by email.)";
      setStatus({
        type: next === "approved" ? "submitted" : "error",
        message: baseMsg + emailNote,
      });
    } catch (err) {
      setStatus({
        type: "error",
        message: err.response?.data?.error || err.message || "Could not record your decision. Try again.",
      });
    } finally {
      setDecidingApproval(false);
    }
  };

  // Admin-only "unlock" for an approved (locked) OPF. Hits
  // lead_details.py's PATCH /leads/<leadId>/opf/make-editable, which flips
  // the record back to draft, clears approval_status/token server-side,
  // and emails the account manager on this lead with a link back to this
  // page. We mirror the approval_status reset locally (setApprovalStatus
  // to null) so the form unlocks immediately without waiting on a reload.
  const [makingEditable, setMakingEditable] = useState(false);

  const handleMakeOpfEditable = async () => {
    if (!effectiveLeadId || makingEditable) return;
    setMakingEditable(true);
    try {
      const res = await api.patch(`/leads/${effectiveLeadId}/opf/make-editable`);
      setApprovalStatus(null);
      setStatus({
        type: "saved",
        message: res.data?.message || "OPF is editable again. The account manager has been notified.",
      });
    } catch (err) {
      setStatus({
        type: "error",
        message: err.response?.data?.message || err.message || "Could not make this OPF editable. Try again.",
      });
    } finally {
      setMakingEditable(false);
    }
  };

  // Once approved: the form locks (no further edits) and Submit becomes
  // available. While blank/pending/declined: the form stays editable, but
  // Submit is disabled with a hover explanation, since nothing can be
  // raised without an admin's sign-off first.
  const formLocked = approvalStatus === "approved";
  const approvalBlockReason =
    approvalStatus === "approved"
      ? null
      : approvalStatus === "pending"
      ? "Approval is pending from admin side."
      : "First take the approval from admin.";

  // Submit needs both: the lead marked Won (pre-existing rule) AND the OPF
  // approved by an admin (new rule) - either one missing keeps it disabled.
  const isSubmitAllowed = isLeadWon && approvalStatus === "approved";

  // ---- Attachments -------------------------------------------------------
  // opf_attachment rows only ever have an FK to a *saved* opf_id, so files
  // picked before the OPF has been saved once (opfId still null) are held
  // here and uploaded automatically the moment Save/Submit gives us one.
  // Files picked once opfId is already known (e.g. reopening a draft) are
  // uploaded immediately instead of being staged.
  const [attachments, setAttachments] = useState([]); // [{opfat_id, opfat_attachment_name, opfat_attachment_type}]
  const [pendingAttachments, setPendingAttachments] = useState([]); // File[] not yet uploaded

  // ---- Comments ----------------------------------------------------------
  // Same opf_notes table as the "Internal Team Notes" card on Deal Details
  // (rows are keyed by lead, so comments written here show up there too and
  // work even before the OPF has been saved).
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  const fetchComments = async () => {
    if (!effectiveLeadId) return;
    try {
      const res = await api.get(`/deal-details/read_lead_notes/${effectiveLeadId}`);
      setComments(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load comments", err);
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !effectiveLeadId || postingComment) return;
    setPostingComment(true);
    try {
      await api.post("/deal-details/add_lead_notes", {
        lead_id: effectiveLeadId,
        note: newComment,
      });
      setNewComment("");
      await fetchComments();
    } catch (err) {
      console.error("Failed to post comment", err);
    } finally {
      setPostingComment(false);
    }
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLeadId]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");

  // Powers the "Save details" modal - lets the user pick between emailing
  // the admin and doing a plain draft save.
  const { promptSaveOptions, saveOptionsState, handlePickEmail, handlePickDraft, handleDismiss } =
    useSaveOptions();

  const buildPayload = () => ({
  opfId,
  leadId: effectiveLeadId,
  customerId,
  header,
  shippingAddresses: shippingAddresses.map((a) => ({
    id: a.id,
    label: a.label,
    address: packAddress(a),
  })),
  products: calc.rowsCalc,
  // `charges` is what opf_api.py reads to populate every charge/margin
  // column. It intentionally only stores the six values the user types
  // directly (project management charges, implementation charges, etc.) —
  // everything the sheet *derives* (misc charges, cost of finance 1/2/3yr,
  // distributor margin, GEM fees, end user/vendor PO value, gross margin,
  // gross margin %, net margin) only ever lived in `calc`, so it never
  // reached the backend and stayed at its DB default of 0.00. Merge those
  // computed values in here so whatever is on screen is exactly what gets
  // saved — the backend still does no math of its own, it just stores this.
  charges: {
    ...charges,
    miscCharges: calc.miscCharges,
    costFinance1Yr: calc.costFinance1Yr,
    costFinance2Yr: calc.costFinance2Yr,
    costFinance3Yr: calc.costFinance3Yr,
    distributorMargin: calc.distributorMargin,
    gemFees: calc.gemFees,
    netMargin: calc.netMargin,
    endUserPOValue: calc.endUserPOValue,
    vendorPOValue: calc.vendorPOValue,
    grossMargin: calc.grossMargin,
    // Backend column is `gross_margin_pct`, but opf_api.py reads it off
    // the payload as `grossMarginPercentage` (not `grossMarginPct`).
    grossMarginPercentage: calc.grossMarginPct,
  },
  totals: {
    endUserPOValue: calc.endUserPOValue,
    vendorPOValue: calc.vendorPOValue,
    grossMargin: calc.grossMargin,
    grossMarginPct: calc.grossMarginPct,
    netMargin: calc.netMargin,
  },
});

  // ---- Attachment helpers -------------------------------------------------
  // Loads the attachment list for a saved OPF (deal_details.py's
  // /deal-details/read_attachments/<opf_id>).
  const fetchAttachments = async (targetOpfId) => {
    if (!targetOpfId) return;
    try {
      const res = await api.get(`/deal-details/read_attachments/${targetOpfId}`);
      if (res.data?.success) setAttachments(res.data.data || []);
    } catch (err) {
      console.error("Failed to load attachments", err);
    }
  };

  // Uploads a single file against an already-saved opf_id
  // (/deal-details/add_attachment expects multipart form fields
  // opf_id + attachment, not JSON).
  const uploadAttachmentFile = (file, targetOpfId) => {
    const formData = new FormData();
    formData.append("opf_id", targetOpfId);
    formData.append("attachment", file);
    return api.post("/deal-details/add_attachment", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  // Called right after Save/Submit returns a fresh opfId - flushes any
  // files the user picked before the OPF existed yet.
  // Returns true when every staged file is stored (or there was nothing
  // to upload), false if any failed - callers use that to avoid reporting
  // success / sending an approval request with attachments missing. Files
  // are dropped from the staged list one by one as they land, so a retry
  // never re-uploads files that already made it.
  const uploadPendingAttachments = async (targetOpfId) => {
    if (!pendingAttachments.length) return true;
    if (!targetOpfId) return false;
    setAttachmentError("");
    try {
      for (const file of pendingAttachments) {
        await uploadAttachmentFile(file, targetOpfId);
        setPendingAttachments((prev) => prev.filter((f) => f !== file));
      }
      await fetchAttachments(targetOpfId);
      return true;
    } catch (err) {
      console.error("Failed to upload staged attachments", err);
      setAttachmentError(
        err.response?.data?.message ||
          "One or more attachments failed to upload - the files are still listed below, try again."
      );
      await fetchAttachments(targetOpfId);
      return false;
    }
  };

  // Multiple files can be picked at once (the <input> below has
  // `multiple`); each is either uploaded immediately (opfId already
  // exists) or staged locally until the OPF is first saved.
  const handleAttachmentSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file later
    if (!files.length) return;

    setAttachmentError("");

    if (opfId) {
      setUploadingAttachment(true);
      try {
        for (const file of files) {
          await uploadAttachmentFile(file, opfId);
        }
        await fetchAttachments(opfId);
      } catch (err) {
        setAttachmentError(
          err.response?.data?.message || "Could not upload one or more attachments. Try again."
        );
      } finally {
        setUploadingAttachment(false);
      }
    } else {
      setPendingAttachments((prev) => [...prev, ...files]);
    }
  };

  const removePendingAttachment = (idx) =>
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));

  // Attachments are served behind @jwt_required(), so a plain <a href>
  // can't carry the auth header - fetch as a blob via the shared axios
  // instance (which attaches the token) and trigger the save manually.
  const downloadAttachment = async (attachment) => {
    try {
      const res = await api.get(`/deal-details/download_attachment/${attachment.opfat_id}`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = attachment.opfat_attachment_name || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download attachment", err);
      setAttachmentError("Could not download this attachment.");
    }
  };

  // Removes an already-uploaded attachment for good (deal_details.py's new
  // DELETE /deal-details/delete_attachment/<attachment_id>).
  const deleteUploadedAttachment = async (attachment) => {
    setAttachmentError("");
    setDeletingAttachmentId(attachment.opfat_id);
    try {
      await api.delete(`/deal-details/delete_attachment/${attachment.opfat_id}`);
      setAttachments((prev) => prev.filter((a) => a.opfat_id !== attachment.opfat_id));
    } catch (err) {
      console.error("Failed to delete attachment", err);
      setAttachmentError(
        err.response?.data?.message || "Could not remove this attachment. Try again."
      );
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  // Shared by both modal choices - persists the draft exactly as before,
  // optionally asking the backend to also email the admin (opf_api.py's
  // /save route sends that email when `notifyAdmin` is set on the payload).
  const saveOpf = async (notifyAdmin) => {
    const payload = { ...buildPayload(), notifyAdmin };
    setSaving(true);
    try {
      let result;
      if (onSaveDetails) {
        result = await onSaveDetails(payload);
      } else {
        result = await callOpfApi("save", payload);
      }
      let attachmentsOk = true;
      if (result && result.opfId) {
        setOpfId(result.opfId);
        setOpfNumber(result.opfNumber);
        attachmentsOk = await uploadPendingAttachments(result.opfId);
      }
      const savedMsg = `Saved successfully${result?.opfNumber ? ` (${result.opfNumber})` : ""}.`;
      const emailNote = notifyAdmin
        ? result?.emailSent
          ? " Admin notified by email."
          : " (Could not send the admin email - the draft was still saved.)"
        : "";
      setStatus({
        type: attachmentsOk ? "saved" : "error",
        message:
          savedMsg +
          emailNote +
          (attachmentsOk ? "" : " But one or more attachments failed to upload - they are still listed below, please try again."),
      });
      // Give the success message a moment on screen before leaving the page.
      // Stay on the page if an attachment failed so it isn't lost.
      if (attachmentsOk) setTimeout(() => navigate(`/leads/${effectiveLeadId}`), 1200);
    } catch (err) {
      setStatus({ type: "error", message: err.message || "Could not save details. Try again." });
    } finally {
      setSaving(false);
    }
  };

  // "Save details" no longer saves directly - it opens a modal so the user
  // can choose between a plain draft save and a save-plus-email-to-admin.
  const handleSaveDetails = async () => {
    const choice = await promptSaveOptions();
    if (choice === "email") {
      await saveOpf(true);
    } else if (choice === "draft") {
      await saveOpf(false);
    }
    // choice === null -> user dismissed the modal; do nothing.
  };

  // Saves the OPF (same as the "email admin" choice above) and asks the
  // backend to send the admin an approval email - opf_api.py's /save route
  // should render that email with Approve / Decline buttons whenever
  // `forApproval` is set on the payload (instead of, or in addition to, the
  // plain notification it already sends for `notifyAdmin`). The email's
  // "Review OPF" link just brings the admin to this page - the actual
  // decision is made here via handleDecideApproval below, authenticated,
  // not through a token-based link in the email itself.
  const handleSendForApproval = async () => {
    if (!header.customerName) {
      setStatus({ type: "error", message: "Customer name is required before sending for approval." });
      return;
    }
    if (calc.rowsCalc.every((r) => !r.product && !r.qty)) {
      setStatus({ type: "error", message: "Add at least one product line before sending for approval." });
      return;
    }
    if (
      shippingAddresses.some(
        (a) => !a.name?.trim() || !a.contact?.trim() || !a.email?.trim() || !a.addressLine?.trim() 
      )
    ) {
      setStatus({
        type: "error",
        message: "Name, Contact, Email and shipping address are required for every shipping address (GST is optional).",
      });
      return;
    }
    if (attachments.length === 0 && pendingAttachments.length === 0) {
      setStatus({ type: "error", message: "Add at least one attachment before sending for approval." });
      return;
    }

    setSendingApproval(true);
    try {
      // Attachments picked before the OPF was ever saved are only staged
      // in the browser. Store them FIRST (plain draft save -> upload), so
      // they are on the OPF before the approval request goes out and are
      // never lost; if the upload fails the approval is NOT sent.
      let workingOpfId = opfId;
      if (pendingAttachments.length > 0) {
        const draftPayload = { ...buildPayload(), notifyAdmin: false };
        const draftResult = onSaveDetails
          ? await onSaveDetails(draftPayload)
          : await callOpfApi("save", draftPayload);
        if (!draftResult?.opfId) {
          throw new Error("Could not save the OPF before uploading attachments. Try again.");
        }
        workingOpfId = draftResult.opfId;
        setOpfId(draftResult.opfId);
        setOpfNumber(draftResult.opfNumber);
        const attachmentsOk = await uploadPendingAttachments(draftResult.opfId);
        if (!attachmentsOk) {
          setStatus({
            type: "error",
            message:
              "Attachments could not be uploaded, so the approval request was not sent. Your details are saved - try again.",
          });
          return;
        }
      }

      const payload = {
        ...buildPayload(),
        opfId: workingOpfId,
        notifyAdmin: true,
        forApproval: true,
      };
      let result;
      if (onSaveDetails) {
        result = await onSaveDetails(payload);
      } else {
        result = await callOpfApi("save", payload);
      }
      if (result && result.opfId) {
        setOpfId(result.opfId);
        setOpfNumber(result.opfNumber);
      }
      setApprovalStatus("pending");
      const emailNote = result?.emailSent
        ? " The admin has been emailed with Approve/Decline options."
        : " (Could not send the approval email - the draft was still saved.)";
      setStatus({
        type: "saved",
        message: `Sent for approval${""}.${emailNote}`,
      });
    } catch (err) {
      setStatus({ type: "error", message: err.message || "Could not send for approval. Try again." });
    } finally {
      setSendingApproval(false);
    }
  };

  // While a request is pending, poll for the admin's decision every 15s so
  // the status pill updates live if this tab is left open (e.g. Approve /
  // Decline clicked from the admin's inbox on another device). Expects a
  // GET /opf/<opfId>/approval-status route returning { approvalStatus }.
  // Fails silently - if that endpoint doesn't exist yet on the backend, the
  // pill simply won't auto-refresh until the page is reloaded.
  useEffect(() => {
    if (!opfId || approvalStatus !== "pending") return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/opf/${opfId}/approval-status`);
        const next = res.data?.approvalStatus;
        if (next && next !== "pending") {
          setApprovalStatus(next);
          setStatus({
            type: next === "approved" ? "submitted" : "error",
            message:
              next === "approved"
                ? "This opf has been approved by the admin."
                : "This opf was declined by the admin.",
          });
        }
      } catch {
        // Endpoint not available yet / transient network issue - ignore.
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [opfId, approvalStatus]);

  // Refresh the GST suggestion list whenever the selected customer
  // changes: their own GST plus any GST(s) used on their earlier OPFs.
  useEffect(() => {
    if (!customerId && !effectiveLeadId) {
      setGstSuggestions([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(`/opf/gst-suggestions/${customerId || 0}`, {
          params: effectiveLeadId ? { lead_id: effectiveLeadId } : {},
        });
        if (!cancelled && res.data?.success) {
          // Defensive: some rows store "no GST" as a literal "-" rather
          // than blank/NULL, so filter those out here too even though
          // the backend already excludes them.
          const NO_VALUE = new Set(["-", "--", "n/a", "na", "none", "nil"]);
          const cleaned = (res.data.suggestions || []).filter(
            (g) => g && !NO_VALUE.has(String(g).trim().toLowerCase())
          );
          setGstSuggestions(cleaned);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load GST suggestions", err);
          setGstSuggestions([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerId, effectiveLeadId]);

  // Refresh the Name / Contact / Email / Address suggestion lists whenever
  // the customer (or the opportunity) changes.
  useEffect(() => {
    const empty = { names: [], contacts: [], emails: [], addresses: [] };
    if (!customerId && !effectiveLeadId) {
      setContactSuggestions(empty);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(`/opf/contact-suggestions/${customerId || 0}`, {
          params: effectiveLeadId ? { lead_id: effectiveLeadId } : {},
        });
        if (!cancelled && res.data?.success) {
          setContactSuggestions({
            names: res.data.names || [],
            contacts: res.data.contacts || [],
            emails: res.data.emails || [],
            addresses: res.data.addresses || [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load contact suggestions", err);
          setContactSuggestions(empty);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerId, effectiveLeadId]);

  const handleSubmit = async () => {
    if (!header.customerName) {
      setStatus({ type: "error", message: "Customer name is required before submitting." });
      return;
    }
    if (calc.rowsCalc.every((r) => !r.product && !r.qty)) {
      setStatus({ type: "error", message: "Add at least one product line before submitting." });
      return;
    }
    if (
      shippingAddresses.some(
        (a) => !a.name?.trim() || !a.contact?.trim() || !a.email?.trim() || !a.addressLine?.trim() 
      )
    ) {
      setStatus({
        type: "error",
        message: "Name, Contact, Email and shipping address are required for every shipping address (GST is optional).",
      });
      return;
    }
    if (attachments.length === 0 && pendingAttachments.length === 0) {
      setStatus({ type: "error", message: "Add at least one attachment before submitting." });
      return;
    }
    const payload = buildPayload();
    setSubmitting(true);
    try {
      let result;
      if (onSubmit) {
        result = await onSubmit(payload);
      } else {
        result = await callOpfApi("submit", payload);
      }
      let attachmentsOk = true;
      if (result && result.opfId) {
        setOpfId(result.opfId);
        setOpfNumber(result.opfNumber);
        attachmentsOk = await uploadPendingAttachments(result.opfId);
      }
      setStatus({
        type: attachmentsOk ? "submitted" : "error",
        message:
          `Submitted successfully${result?.opfNumber ? ` (${result.opfNumber})` : ""}.` +
          (attachmentsOk ? "" : " But one or more attachments failed to upload - they are still listed below, please try again."),
      });
      // The lead → OPF handoff data has now been consumed into a saved
      // record, so it's safe to clear it - this keeps it around across
      // refreshes up until this point, but stops it leaking into a later,
      // unrelated visit to this form.
      localStorage.removeItem("customer_name");
      localStorage.removeItem("lead_id");
      // Give the success message a moment on screen before leaving the page.
      // Stay on the page if an attachment failed so it isn't lost.
      if (attachmentsOk) setTimeout(() => navigate(`/leads/${effectiveLeadId}`), 1200);
    } catch (err) {
      setStatus({ type: "error", message: err.message || "Submission failed. Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const thStyle = {
    padding: "8px 6px",
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#fff",
    textAlign: "left",
    whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "5px 6px",
    borderBottom: "1px solid #e2e8f0",
    fontSize: "13px",
  };
  const inputCellStyle = {
    width: "100%",
    padding: "6px 7px",
    fontSize: "12.5px",
    border: "1px solid #cbd5e1",
    borderRadius: "5px",
    boxSizing: "border-box",
    color: "#0f172a",
  };
  const readOnlyCellStyle = {
    ...inputCellStyle,
    background: "#f1f5f9",
    color: "#334155",
    border: "1px solid #e2e8f0",
    fontWeight: 500,
  };

  const btnGhost = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "7px 12px",
    fontSize: "12.5px",
    fontWeight: 500,
    color: "#1e3a5f",
    background: "#fff",
    border: "1px solid #1e3a5f",
    borderRadius: "6px",
    cursor: "pointer",
  };

  useEffect(() => {
    (async () => {
      // Customers list must be loaded first so a prefilled customerName
      // (from either a draft or the lead handoff) can be matched to a
      // customer id.
      await fetchDropdowns();

      if (effectiveLeadId) {
        await loadExistingDraft(effectiveLeadId);
      }
    })();
  }, []);

  // Checks whether this lead already has a DRAFT OPF sitting against it.
  // If so, the whole form is prefilled from that record (and further
  // saves/submits update it in place via opfId). If not, the form simply
  // stays as a fresh, blank OPF - nothing to undo, nothing to reset.
  const loadExistingDraft = async (effectiveLeadId) => {
    setLoadingDraft(true);
    try {
      const res = await api.get(`/opf/by-lead/${effectiveLeadId}`);
      const order = res.data?.order;
      if (!order) return; // No draft for this lead yet - fresh form stands.

      setOpfId(order.opf_id);
      setOpfNumber(order.opf_number);
      setCustomerId(order.cust_id ?? null);
      fetchAttachments(order.opf_id);

      setHeader({
        customerName: order.cust_name || "",
        distributorName: order.distributor_name || "",
        billingAddress: order.billing_address || "",
        product: order.product_summary || "",
        contactPerson: order.end_customer_contact || "",
        vendorPaymentTerms: order.vendor_payment_terms || "",
        dropshipment: order.dropshipment || "",
        phone: order.phone || "",
        email: order.email || "",
        poNo: order.po_no || "",
        customerPaymentTerms: order.customer_payment_terms || "",
        deliveryTerms: order.delivery_terms || "",
        locationImplementation: order.location_implementation || "",
        locationOnsite: order.location_onsite_support || "",
      });

      const loadedAddresses = res.data?.shippingAddresses || [];
      if (loadedAddresses.length) {
        setShippingAddresses(
          loadedAddresses.map((a) => ({
            id: a.shipping_id,
            label: a.label || "",
            ...splitPackedAddress(a.address),
          }))
        );
      }

      const loadedProducts = res.data?.products || [];
      if (loadedProducts.length) {
        setRows(
          loadedProducts.map((p) => ({
            id: p.line_id,
            oem: p.oem || "",
            product: p.product || "",
            qty: p.qty ?? "",
            unitPrice: p.unit_price ?? "",
            vendorUnitPrice: p.vendor_unit_price ?? "",
            shippingAddressId: p.shipping_id ?? "",
          }))
        );
      }

      // Keep the shared id counter ahead of every id just loaded from the
      // database, so any new rows/addresses added from here on can't
      // collide with the ones prefilled above.
      const loadedIds = [
        ...loadedAddresses.map((a) => a.shipping_id),
        ...loadedProducts.map((p) => p.line_id),
      ].filter((n) => typeof n === "number" && !Number.isNaN(n));
      if (loadedIds.length) {
        uid = Math.max(uid, ...loadedIds) + 1;
      }

      setCharges((prev) => ({
        ...prev,
        projectManagementCharges: order.project_management_charges ?? "",
        implementationCharges: order.implementation_charges ?? "",
        postImplementationSupportCharges: order.post_implementation_support_charges ?? "",
        costOfFinancePBG: order.cost_of_finance_pbg ?? "",
        logisticInsurance: order.logistic_insurance ?? "",
        gemTenderPOValue: order.gem_tender_po_value ?? "",
        // These six are also user-editable overrides (see `overrideOr` in
        // calc) — without restoring them here, overrideOr sees them as
        // unset on reopen and silently recomputes the default formula
        // instead of showing what was actually saved.
        miscCharges: order.misc_charges ?? "",
        costFinance1Yr: order.cost_finance_1yr ?? "",
        costFinance2Yr: order.cost_finance_2yr ?? "",
        costFinance3Yr: order.cost_finance_3yr ?? "",
        distributorMargin: order.distributor_margin ?? "",
        gemFees: order.gem_fees ?? "",
      }));

      setStatus({
        type: "saved",
        message: `Draft loaded  — continue where you left off.`,
      });

      // `approval_status` is only ever set once "Send for Approval" has been
      // used at least once for this OPF - a fresh/never-sent draft won't
      // have the column set at all, which is the same as no request yet.
      setApprovalStatus(order.approval_status || null);
    } catch (err) {
      // A missing draft is the normal, expected case for a fresh lead and
      // never reaches here (the endpoint returns {order: null} with a 200
      // for that). Anything that lands in this catch is a real failure -
      // e.g. a bad lead id, network error, or auth issue - so surface it
      // instead of silently leaving the person on an unexplained blank form.
      console.error("Failed to check for an existing OPF draft", err);
      setStatus({
        type: "error",
        message:
          "Couldn't check for an existing draft for this lead — starting a fresh form. " +
          (err.response?.data?.error || err.message || ""),
      });
    } finally {
      setLoadingDraft(false);
    }
  };

const fetchDropdowns = async () => {
  try {
    const res = await api.get("/deals/dropdowns");

    if (res.data.success) {
      const customerList = res.data.customers.map((c) => ({
        id: c.cust_id,
        name: c.cust_name,
        gst: c.cust_gst,
      }));

      setCustomers(customerList);

      // Preselect customer if passed
      const selectedName = storedCustomerName || defaultCustomerName;

if (selectedName) {
  const selectedCustomer = customerList.find(
    (c) => c.name === selectedName
  );

  setHeader((prev) => ({
    ...prev,
    customerName: selectedName,
  }));

  setCustomerId(selectedCustomer?.id || null);
}
    }
  } catch (err) {
    console.error("Failed to load customers", err);
  }
};

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: "#0f172a",
        padding: "clamp(14px, 4vw, 24px) clamp(14px, 4vw, 32px) 100px",
        background: "#f8fafc",
      }}
    >
      <SaveOptionsDialog
        saveOptionsState={saveOptionsState}
        onPickEmail={handlePickEmail}
        onPickDraft={handlePickDraft}
        onDismiss={handleDismiss}
      />

      {/* Back - always goes to this lead's detail page ("1 step back" in
          the app's own hierarchy), rather than browser history via
          navigate(-1). That broke whenever this page is the first thing
          loaded in the tab - e.g. opened straight from the "Review OPF" /
          "OPF made editable" email links - since there's no prior in-app
          entry for -1 to land on. */}
      <button
        type="button"
        onClick={() => navigate(`/leads/${effectiveLeadId}`)}
        disabled={!effectiveLeadId}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "7px 12px",
          fontSize: "12.5px",
          fontWeight: 500,
          color: "#1e3a5f",
          background: "#fff",
          border: "1px solid #cbd5e1",
          borderRadius: "6px",
          cursor: effectiveLeadId ? "pointer" : "not-allowed",
          opacity: effectiveLeadId ? 1 : 0.6,
          marginBottom: "14px",
        }}
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div
        style={{
          background: "#1e3a5f",
          borderRadius: "10px",
          padding: "clamp(14px, 3vw, 22px) clamp(16px, 4vw, 28px)",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#fff" }}>
            Order Processing Form
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#b8c7dc" }}>
            Customer, distributor, product and margin details
          </p>
        </div>
        <div
  style={{
    background: "rgba(255,255,255,0.12)",
    borderRadius: "8px",
    padding: "10px 16px",
    color: "#e2e8f0",
    minWidth: "260px",
  }}
>
 

  <div
    style={{
      marginTop: "8px",
      fontSize: "12px",
      opacity: 0.8,
    }}
  >
    Opportunity ID
  </div>
  <div
    style={{
      fontSize: "15px",
      fontWeight: 600,
      fontFamily: "monospace",
    }}
  >
    {effectiveLeadId || leadId || "-"}
  </div>
</div>
      </div>

      {status && (
        <div
          style={{
            marginBottom: "18px",
            padding: "10px 14px",
            borderRadius: "7px",
            fontSize: "13px",
            fontWeight: 500,
            background:
              status.type === "error" ? "#fef2f2" : status.type === "submitted" ? "#ecfdf5" : "#eff6ff",
            color: status.type === "error" ? "#b91c1c" : status.type === "submitted" ? "#166534" : "#1d4ed8",
            border:
              "1px solid " +
              (status.type === "error" ? "#fecaca" : status.type === "submitted" ? "#bbf7d0" : "#bfdbfe"),
          }}
        >
          {status.message}
        </div>
      )}

      {loadingDraft && (
        <div
          style={{
            marginBottom: "18px",
            padding: "10px 14px",
            borderRadius: "7px",
            fontSize: "13px",
            fontWeight: 500,
            background: "#eff6ff",
            color: "#1d4ed8",
            border: "1px solid #bfdbfe",
          }}
        >
          Checking for an existing draft OPF for this lead…
        </div>
      )}

      {/* Customer / Distributor details */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          padding: "clamp(14px, 3vw, 20px) clamp(14px, 3vw, 24px)",
          marginBottom: "18px",
        }}
      >
        <SectionHeader icon={FileText} title="Customer & distributor details" />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            columnGap: "28px",
            rowGap: "18px",
          }}
        >
          <div>
            <CustomerNameField
  value={header.customerName}
  onChange={(name) => {
    setH("customerName")(name);

    const selected = customers.find((c) => c.name === name);
    setCustomerId(selected?.id || null);
  }}
  customers={
    header.customerName && !customers.some((c) => c.name === header.customerName)
      ? [...customers, { id: customerId || "", name: header.customerName }]
      : customers
  }
  onAddCustomer={addCustomer}
  disabled={true}
/>
            <Field
              label="Billing address with GST"
              value={header.billingAddress}
              onChange={setH("billingAddress")}
              textarea
              disabled={formLocked}
            />
            <Field
              label="End customer contact person"
              value={header.contactPerson}
              onChange={setH("contactPerson")}
              disabled={formLocked}
            />
            <Field label="Phone" value={header.phone} onChange={setH("phone")} disabled={formLocked} />
            <Field label="Email" value={header.email} onChange={setH("email")} type="email" disabled={formLocked} />
            <Field label="PO No." value={header.poNo} onChange={setH("poNo")} disabled={formLocked} />
            <SuggestionField
  label="Customer payment terms"
  value={header.customerPaymentTerms}
  onChange={setH("customerPaymentTerms")}
  options={PAYMENT_TERMS_OPTIONS}
  disabled={formLocked}
/>
            <Field label="Delivery terms" value={header.deliveryTerms} onChange={setH("deliveryTerms")} disabled={formLocked} />
            <Field
              label="Location for implementation"
              value={header.locationImplementation}
              onChange={setH("locationImplementation")}
              textarea
              disabled={formLocked}
            />
            <Field
              label="Location for onsite support"
              value={header.locationOnsite}
              onChange={setH("locationOnsite")}
              textarea
              disabled={formLocked}
            />
          </div>
          <div>
            <Field
              label="Distributor name / vendor name"
              value={header.distributorName}
              onChange={setH("distributorName")}
              disabled={formLocked}
            />
            <Field label="Product" value={header.product} onChange={setH("product")} disabled={formLocked} />
            <Field
              label="Distributor / vendor payment terms"
              value={header.vendorPaymentTerms}
              onChange={setH("vendorPaymentTerms")}
              disabled={formLocked}
            />
            <SelectField
              label="Dropshipment"
              value={header.dropshipment}
              onChange={setH("dropshipment")}
              options={DROPSHIPMENT_OPTIONS}
              disabled={formLocked}
            />
          </div>
        </div>
      </div>

      {/* Shipping addresses */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          padding: "clamp(14px, 3vw, 20px) clamp(14px, 3vw, 24px)",
          marginBottom: "18px",
        }}
      >
        <SectionHeader
          icon={MapPin}
          title="Shipping addresses"
          subtitle="Add one or more delivery locations and assign each product line to one"
        />
        {shippingAddresses.map((a, idx) => (
          <div
            key={a.id}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "16px 18px",
              marginBottom: "14px",
              background: "#fbfcfe",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
              }}
            >
              <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e3a5f" }}>
                Shipping address {idx + 1}
              </div>
              <button
                onClick={() => removeAddr(a.id)}
                disabled={shippingAddresses.length === 1 || formLocked}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "5px 10px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  background: "#fff",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: shippingAddresses.length === 1 || formLocked ? "not-allowed" : "pointer",
                  color: shippingAddresses.length === 1 || formLocked ? "#cbd5e1" : "#b91c1c",
                }}
                aria-label="Remove address"
              >
                <Trash2 size={13} /> Remove
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "12px",
                marginBottom: "14px",
              }}
            >
              <AddrField
                label="Label"
                placeholder={`e.g. Site ${idx + 1}`}
                value={a.label}
                onChange={(v) => updateAddr(a.id, "label", v)}
                disabled={formLocked}
              />
              <AddrSuggestionField
                label="Name *"
                placeholder="Contact name"
                value={a.name}
                onChange={(v) => updateAddr(a.id, "name", v)}
                options={contactSuggestions.names}
                listId={`name-suggestions-${a.id}`}
                disabled={formLocked}
              />
              <AddrSuggestionField
                label="Contact *"
                placeholder="Phone number"
                value={a.contact}
                onChange={(v) => updateAddr(a.id, "contact", v)}
                options={contactSuggestions.contacts}
                listId={`contact-suggestions-${a.id}`}
                disabled={formLocked}
              />
              <AddrSuggestionField
                label="Email *"
                type="email"
                placeholder="name@example.com"
                value={a.email}
                onChange={(v) => updateAddr(a.id, "email", v)}
                options={contactSuggestions.emails}
                listId={`email-suggestions-${a.id}`}
                disabled={formLocked}
              />
              <AddrSuggestionField
                label="GST *"
                placeholder="Optional"
                value={a.gst}
                onChange={(v) => updateAddr(a.id, "gst", v)}
                options={gstSuggestions}
                listId={`gst-suggestions-${a.id}`}
                disabled={formLocked}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <AddrSuggestionField
                label="Address *"
                placeholder="Street, city, state, PIN code"
                value={a.addressLine}
                onChange={(v) => updateAddr(a.id, "addressLine", v)}
                options={contactSuggestions.addresses}
                listId={`address-suggestions-${a.id}`}
                disabled={formLocked}
              />
            </div>

            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "5px",
                }}
              >
                <label style={{ fontSize: "11.5px", fontWeight: 500, color: "#64748b" }}>
                  Full shipping address
                </label>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    padding: "2px 7px",
                    borderRadius: "999px",
                    background: "#e2e8f0",
                    color: "#475569",
                  }}
                >
                  AUTO-GENERATED
                </span>
              </div>
              <textarea
                readOnly
                value={packAddress(a)}
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: "12.5px",
                  fontFamily: "inherit",
                  border: "1px dashed #cbd5e1",
                  borderRadius: "6px",
                  background: "#f1f5f9",
                  color: "#64748b",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        ))}
        <button
          onClick={addAddr}
          disabled={formLocked}
          style={{ ...btnGhost, cursor: formLocked ? "not-allowed" : "pointer", opacity: formLocked ? 0.6 : 1 }}
        >
          <Plus size={14} /> Add shipping address
        </button>
      </div>

      {/* Product table */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          padding: "clamp(14px, 3vw, 20px) clamp(14px, 3vw, 24px)",
          marginBottom: "18px",
        }}
      >
        <SectionHeader icon={Package} title="Product line items" />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "980px" }}>
            <thead>
              <tr style={{ background: "#1e3a5f" }}>
                <th style={thStyle}>OEM</th>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Unit price (excl. GST)</th>
                <th style={thStyle}>Total PO value (A)</th>
                <th style={thStyle}>Vendor unit price</th>
                <th style={thStyle}>Total PO value vendor</th>
                <th style={thStyle}>Shipping address</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {calc.rowsCalc.map((r) => (
                <tr key={r.id}>
                  <td style={tdStyle}>
                    <input
                      value={r.oem}
                      disabled={formLocked}
                      onChange={(e) => updateRow(r.id, "oem", e.target.value)}
                      style={{ ...inputCellStyle, background: formLocked ? "#f1f5f9" : undefined }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      value={r.product}
                      disabled={formLocked}
                      onChange={(e) => updateRow(r.id, "product", e.target.value)}
                      style={{ ...inputCellStyle, background: formLocked ? "#f1f5f9" : undefined }}
                    />
                  </td>
                  <td style={{ ...tdStyle, width: "70px" }}>
                    <input
                      type="number"
                      value={r.qty}
                      disabled={formLocked}
                      onChange={(e) => updateRow(r.id, "qty", e.target.value)}
                      style={{ ...inputCellStyle, background: formLocked ? "#f1f5f9" : undefined }}
                    />
                  </td>
                  <td style={{ ...tdStyle, width: "120px" }}>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "12.5px",
                          color: "#64748b",
                          pointerEvents: "none",
                        }}
                      >
                        ₹
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatIndianCurrency(r.unitPrice)}
                        disabled={formLocked}
                        onChange={(e) => {
                          const raw = unformatCurrency(e.target.value);
                          if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
                          updateRow(r.id, "unitPrice", raw);
                        }}
                        style={{ ...inputCellStyle, paddingLeft: "20px", background: formLocked ? "#f1f5f9" : undefined }}
                      />
                    </div>
                  </td>
                  <td style={{ ...tdStyle, width: "130px" }}>
                    <div style={readOnlyCellStyle}>{fmt(r.totalPOValue)}</div>
                  </td>
                  <td style={{ ...tdStyle, width: "120px" }}>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "12.5px",
                          color: "#64748b",
                          pointerEvents: "none",
                        }}
                      >
                        ₹
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatIndianCurrency(r.vendorUnitPrice)}
                        disabled={formLocked}
                        onChange={(e) => {
                          const raw = unformatCurrency(e.target.value);
                          if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
                          updateRow(r.id, "vendorUnitPrice", raw);
                        }}
                        style={{ ...inputCellStyle, paddingLeft: "20px", background: formLocked ? "#f1f5f9" : undefined }}
                      />
                    </div>
                  </td>
                  <td style={{ ...tdStyle, width: "130px" }}>
                    <div style={readOnlyCellStyle}>{fmt(r.totalPOValueVendor)}</div>
                  </td>
                  <td style={{ ...tdStyle, width: "150px" }}>
                    <select
                      value={r.shippingAddressId}
                      disabled={formLocked}
                      onChange={(e) => updateRow(r.id, "shippingAddressId", e.target.value)}
                      style={{ ...inputCellStyle, background: formLocked ? "#f1f5f9" : undefined }}
                    >
                      {/* <option value="">Select…</option> */}
                      {shippingAddresses.map((a, i) => (
                        <option key={a.id} value={a.id}>
                          {a.label || `Select`}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, width: "36px" }}>
                    <button
                      onClick={() => removeRow(r.id)}
                      disabled={rows.length === 1 || formLocked}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        background: "#fff",
                        cursor: rows.length === 1 || formLocked ? "not-allowed" : "pointer",
                        color: "#94a3b8",
                        height: "34px",
                        width: "34px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-label="Remove row"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          onClick={addRow}
          disabled={formLocked}
          style={{ ...btnGhost, marginTop: "12px", cursor: formLocked ? "not-allowed" : "pointer", opacity: formLocked ? 0.6 : 1 }}
        >
          <Plus size={14} /> Add product line
        </button>

        {/* PO value summary */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "12px",
            marginTop: "20px",
          }}
        >
          {[
            ["End user PO value", fmt(calc.endUserPOValue)],
            ["Vendor PO value", fmt(calc.vendorPOValue)],
            ["Gross margin", fmt(calc.grossMargin)],
            ["Gross margin %", fmtPct(calc.grossMarginPct)],
          ].map(([label, val]) => (
            <div
              key={label}
              style={{
                background: "#f1f5f9",
                borderRadius: "8px",
                padding: "12px 14px",
              }}
            >
              <div style={{ fontSize: "11.5px", color: "#64748b", marginBottom: "4px" }}>
                {label}
              </div>
              <div style={{ fontSize: "17px", fontWeight: 700, color: "#1e3a5f" }}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Attachments */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          padding: "clamp(14px, 3vw, 20px) clamp(14px, 3vw, 24px)",
          marginBottom: "18px",
        }}
      >
        <SectionHeader
          icon={Paperclip}
          title="Attachments *"
          subtitle="Add one or more supporting files (PO copy, quotation, etc.) — required before submitting"
        />

        {attachmentError && (
          <div
            style={{
              marginBottom: "12px",
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "12.5px",
              fontWeight: 500,
              background: "#fef2f2",
              color: "#b91c1c",
              border: "1px solid #fecaca",
            }}
          >
            {attachmentError}
          </div>
        )}

        {/* Already-uploaded attachments (only exist once the OPF has been saved once) */}
        {attachments.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            {attachments.map((att) => (
              <div
                key={att.opfat_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  padding: "9px 12px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "7px",
                  marginBottom: "8px",
                  background: "#fbfcfe",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                  <Paperclip size={14} color="#64748b" style={{ flexShrink: 0 }} />
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#0f172a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={att.opfat_attachment_name}
                  >
                    {att.opfat_attachment_name}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => downloadAttachment(att)}
                    disabled={deletingAttachmentId === att.opfat_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "5px 10px",
                      border: "1px solid #1e3a5f",
                      borderRadius: "6px",
                      background: "#fff",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#1e3a5f",
                      cursor: deletingAttachmentId === att.opfat_id ? "not-allowed" : "pointer",
                      opacity: deletingAttachmentId === att.opfat_id ? 0.6 : 1,
                    }}
                  >
                    <Download size={13} /> Download
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteUploadedAttachment(att)}
                    disabled={deletingAttachmentId === att.opfat_id || formLocked}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      background: "#fff",
                      color: deletingAttachmentId === att.opfat_id || formLocked ? "#cbd5e1" : "#b91c1c",
                      cursor: deletingAttachmentId === att.opfat_id || formLocked ? "not-allowed" : "pointer",
                    }}
                    aria-label="Remove attachment"
                    title="Remove attachment"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Files picked before the OPF has ever been saved - staged locally,
            uploaded automatically the moment Save/Submit succeeds. */}
        {pendingAttachments.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            {pendingAttachments.map((file, idx) => (
              <div
                key={`${file.name}-${idx}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  padding: "9px 12px",
                  border: "1px dashed #cbd5e1",
                  borderRadius: "7px",
                  marginBottom: "8px",
                  background: "#f8fafc",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                  <Paperclip size={14} color="#64748b" style={{ flexShrink: 0 }} />
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#334155",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </span>
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                      padding: "2px 7px",
                      borderRadius: "999px",
                      background: "#fef3c7",
                      color: "#92400e",
                      flexShrink: 0,
                    }}
                  >
                    PENDING SAVE
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removePendingAttachment(idx)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "26px",
                    height: "26px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    background: "#fff",
                    color: "#94a3b8",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  aria-label="Remove file"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <label
          style={{
            ...btnGhost,
            display: "inline-flex",
            opacity: uploadingAttachment || formLocked ? 0.6 : 1,
            cursor: uploadingAttachment || formLocked ? "not-allowed" : "pointer",
          }}
        >
          <Plus size={14} />
          {uploadingAttachment ? "Uploading…" : "Add attachment(s)"}
          <input
            type="file"
            multiple
            onChange={handleAttachmentSelect}
            disabled={uploadingAttachment || formLocked}
            style={{ display: "none" }}
          />
        </label>
        {!opfId && (
          <p style={{ margin: "8px 0 0", fontSize: "11.5px", color: "#94a3b8" }}>
            Files are uploaded once you Save details or Submit for the first time.
          </p>
        )}
      </div>

      {/* Charges and net margin */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          padding: "clamp(14px, 3vw, 20px) clamp(14px, 3vw, 24px)",
          marginBottom: "18px",
        }}
      >
        <SectionHeader icon={Calculator} title="Charges & net margin" />
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: "480px", borderCollapse: "collapse" }}>
          <tbody>
            <ChargeInputRow
  label="Project management charges (if any)"
  value={charges.projectManagementCharges}
  onChange={setC("projectManagementCharges")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Misc charges (10% of project management charges)"
  value={calc.miscCharges}
  onChange={setC("miscCharges")}
  disabled={formLocked}
/>

<ChargeInputRow
  label='Implementation charges "E"'
  value={charges.implementationCharges}
  onChange={setC("implementationCharges")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Post implementation support charges (F)"
  value={charges.postImplementationSupportCharges}
  onChange={setC("postImplementationSupportCharges")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Cost of finance of PBG (G)"
  value={charges.costOfFinancePBG}
  onChange={setC("costOfFinancePBG")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Cost of finance if customer terms exceed vendor terms — 1 year (3.125% of End user PO value)"
  value={calc.costFinance1Yr}
  onChange={setC("costFinance1Yr")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Cost of finance if customer terms exceed vendor terms — 2 years (11.25% of End user PO value)"
  value={calc.costFinance2Yr}
  onChange={setC("costFinance2Yr")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Cost of finance if customer terms exceed vendor terms — 3 years (24.375% of End user PO value)"
  value={calc.costFinance3Yr}
  onChange={setC("costFinance3Yr")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Logistics and insurance (I)"
  value={charges.logisticInsurance}
  onChange={setC("logisticInsurance")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="Distributor margin (0.5% of Vendor PO value)"
  value={calc.distributorMargin}
  onChange={setC("distributorMargin")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="GEM tender PO value including GST"
  value={charges.gemTenderPOValue}
  onChange={setC("gemTenderPOValue")}
  disabled={formLocked}
/>

<ChargeInputRow
  label="GEM fees (0.5% of GEM tender PO value)"
  value={calc.gemFees}
  onChange={setC("gemFees")}
  disabled={formLocked}
/>
           
            <tr>
              <td
                style={{
                  padding: "14px 8px",
                  fontSize: "14.5px",
                  fontWeight: 700,
                  color: "#0f172a",
                  borderTop: "2px solid #1e3a5f",
                }}
              >
                Net margin
              </td>
              <td
                style={{
                  padding: "14px 8px",
                  fontSize: "16.5px",
                  fontWeight: 700,
                  color: calc.netMargin >= 0 ? "#166534" : "#b91c1c",
                  textAlign: "right",
                  borderTop: "2px solid #1e3a5f",
                }}
              >
                {fmt(calc.netMargin)}
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      {/* Comments */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          padding: "clamp(10px, 2.5vw, 14px) clamp(12px, 2.5vw, 18px)",
          marginBottom: "18px",
        }}
      >
        <SectionHeader icon={MessageSquare} title="Comments" />
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          rows={2}
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            padding: "8px 10px",
            fontSize: "13px",
            resize: "none",
            fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          onClick={addComment}
          disabled={postingComment || !newComment.trim()}
          style={{
            marginTop: "8px",
            width: "auto",
            padding: "6px 16px",
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: 500,
            cursor: postingComment || !newComment.trim() ? "not-allowed" : "pointer",
            opacity: postingComment || !newComment.trim() ? 0.6 : 1,
          }}
        >
          {postingComment ? "Posting…" : "Post Comment"}
        </button>
        <div
          style={{
            marginTop: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxHeight: "160px",
            overflow: "auto",
          }}
        >
          {comments.map((c) => (
            <div
              key={c.opfnts_id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "8px 10px",
                background: "#f9fafb",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "11.5px", fontWeight: 600, color: "#374151" }}>
                  {c.usr_name}
                </span>
                <span style={{ fontSize: "10.5px", color: "#6b7280" }}>
                  written by: {c.usr_name}
                  {c.posted_at ? ` · ${formatCommentTime(c.posted_at)}` : ""}
                </span>
              </div>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#4b5563" }}>
                {c.opfnts_note}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Action bar */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "12px",
          padding: "14px 20px",
          marginTop: "8px",
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          boxShadow: "0 -2px 10px rgba(15, 23, 42, 0.06)",
        }}
      >
        {isAdmin && approvalStatus === "approved" && (
          <button
            type="button"
            onClick={handleMakeOpfEditable}
            disabled={makingEditable}
            style={{
              padding: "10px 18px",
              fontSize: "13.5px",
              fontWeight: 600,
              color: "#1e3a5f",
              background: "#fff",
              border: "1px solid #1e3a5f",
              borderRadius: "7px",
              cursor: makingEditable ? "not-allowed" : "pointer",
              opacity: makingEditable ? 0.6 : 1,
            }}
          >
            {makingEditable ? "Unlocking…" : "Make the Opf Editable"}
          </button>
        )}

        {isAdmin && approvalStatus === "pending" && (
          <div style={{ display: "inline-flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => handleDecideApproval("approved")}
              disabled={decidingApproval}
              style={{
                padding: "10px 18px",
                fontSize: "13.5px",
                fontWeight: 600,
                color: "#fff",
                background: "#059669",
                border: "1px solid #059669",
                borderRadius: "7px",
                cursor: decidingApproval ? "not-allowed" : "pointer",
                opacity: decidingApproval ? 0.6 : 1,
              }}
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => handleDecideApproval("declined")}
              disabled={decidingApproval}
              style={{
                padding: "10px 18px",
                fontSize: "13.5px",
                fontWeight: 600,
                color: "#fff",
                background: "#dc2626",
                border: "1px solid #dc2626",
                borderRadius: "7px",
                cursor: decidingApproval ? "not-allowed" : "pointer",
                opacity: decidingApproval ? 0.6 : 1,
              }}
            >
              Decline
            </button>
          </div>
        )}

        {approvalStatus ? (
          <ApprovalStatusPill status={approvalStatus} onResend={handleSendForApproval} resending={sendingApproval} />
        ) : (
          <button
            type="button"
            onClick={handleSendForApproval}
            disabled={saving || submitting || sendingApproval}
            style={{
              padding: "10px 20px",
              fontSize: "13.5px",
              fontWeight: 600,
              color: "#fff",
              background: "#7c3aed",
              border: "1px solid #7c3aed",
              borderRadius: "7px",
              cursor: saving || submitting || sendingApproval ? "not-allowed" : "pointer",
              opacity: saving || submitting || sendingApproval ? 0.6 : 1,
            }}
          >
            {sendingApproval ? "Sending…" : "Send for Approval"}
          </button>
        )}

        <button
          type="button"
          onClick={handleSaveDetails}
          disabled={saving || submitting || formLocked}
          title={formLocked ? "This OPF is approved and locked. An admin can make it editable again." : undefined}
          style={{
            padding: "10px 20px",
            fontSize: "13.5px",
            fontWeight: 600,
            color: "#1e3a5f",
            background: "#fff",
            border: "1px solid #1e3a5f",
            borderRadius: "7px",
            cursor: saving || submitting || formLocked ? "not-allowed" : "pointer",
            opacity: saving || submitting || formLocked ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Save details"}
        </button>
        {/* Submit is visible to admins only */}
        {isAdmin && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || submitting || !isSubmitAllowed}
            title={
              approvalBlockReason
                ? approvalBlockReason
                : !isLeadWon
                ? "Submit is available once the lead status is Won"
                : undefined
            }
            style={{
              padding: "10px 22px",
              fontSize: "13.5px",
              fontWeight: 600,
              color: "#fff",
              background: "#059669",
              border: "1px solid #059669",
              borderRadius: "7px",
              cursor: saving || submitting || !isSubmitAllowed ? "not-allowed" : "pointer",
              opacity: saving || submitting || !isSubmitAllowed ? 0.6 : 1,
            }}
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        )}
      </div>
    </div>
  );
}

// Replaces the "Send for Approval" button once a request has gone out.
// Pending/Approved are plain status pills; Declined also offers a small
// "Resend" action so the person isn't stuck if the admin pushes back.
function ApprovalStatusPill({ status, onResend, resending }) {
  const styles = {
    pending: { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8", label: "Pending Approval" },
    approved: { bg: "#ecfdf5", border: "#bbf7d0", color: "#166534", label: "Approved" },
    declined: { bg: "#fef2f2", border: "#fecaca", color: "#b91c1c", label: "Declined" },
  };
  const s = styles[status] || styles.pending;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "9px 16px",
          fontSize: "13px",
          fontWeight: 600,
          color: s.color,
          background: s.bg,
          border: `1px solid ${s.border}`,
          borderRadius: "7px",
        }}
      >
        {status === "pending" && (
          <span
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "50%",
              background: s.color,
              display: "inline-block",
            }}
          />
        )}
        {s.label}
      </span>

      {status === "declined" && (
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          style={{
            padding: "9px 14px",
            fontSize: "12.5px",
            fontWeight: 600,
            color: "#7c3aed",
            background: "#fff",
            border: "1px solid #7c3aed",
            borderRadius: "7px",
            cursor: resending ? "not-allowed" : "pointer",
            opacity: resending ? 0.6 : 1,
          }}
        >
          {resending ? "Sending…" : "Resend for Approval"}
        </button>
      )}
    </div>
  );
}

function ChargeInputRow({ label, value, onChange, disabled = false }) {
  return (
    <tr>
      <td style={{ padding: "8px", fontSize: "13px", color: "#334155", width: "65%" }}>{label}</td>
      <td style={{ padding: "8px", textAlign: "right" }}>
        <div style={{ position: "relative", display: "inline-block" }}>
          <span
            style={{
              position: "absolute",
              left: "9px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "13px",
              color: "#64748b",
              pointerEvents: "none",
            }}
          >
            ₹
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={formatIndianCurrency(value)}
            disabled={disabled}
            onChange={(e) => {
              const raw = unformatCurrency(e.target.value);
              if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
              onChange(raw);
            }}
            placeholder="0"
            style={{
              width: "160px",
              padding: "7px 9px 7px 22px",
              fontSize: "13px",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              textAlign: "right",
              boxSizing: "border-box",
              color: "#0f172a",
              background: disabled ? "#f1f5f9" : "#fff",
            }}
          />
        </div>
      </td>
    </tr>
  );
}

function ChargeCalcRow({ label, value }) {
  return (
    <tr>
      <td style={{ padding: "8px", fontSize: "13px", color: "#64748b", width: "65%" }}>{label}</td>
      <td
        style={{
          padding: "8px",
          fontSize: "13px",
          fontWeight: 500,
          color: "#334155",
          textAlign: "right",
        }}
      >
        {value}
      </td>
    </tr>
  );
}